/**
 * Nosto replaces the collection toolbar after this script loads and changes
 * <show-items-without-model> into a plain <div>. Delegated listeners
 * therefore have to own the interaction; listeners registered by the custom
 * element do not survive the replacement - which is why event handling here
 * is done via document/window delegation rather than inside the class.
 */
const MODEL_VIEW_CHECKBOX_SELECTOR = '.show-items-without-model-input';
const MODEL_VIEW_CONTAINER_SELECTOR = '.enabled-show-items-without-model';
const MODEL_VIEW_ACTIVE_CLASS = 'active-show-items-without-model';
const initializedModelViewCheckboxes = new WeakSet();

let modelViewCarouselTimer;

function getFlickityInstance(carousel) {
    // PLP sliders are created by the Flickity constructor bundled in
    // themeVendor. The separately loaded window.Flickity has a different
    // instance registry, so window.Flickity.data() cannot always find them.
    if (carousel.flickityInstance) {
        return carousel.flickityInstance;
    }

    if (window.themeVendor?.Flickity) {
        const themeInstance = window.themeVendor.Flickity.data(carousel);
        if (themeInstance) {
            return themeInstance;
        }
    }

    if (window.Flickity) {
        return window.Flickity.data(carousel);
    }

    return null;
}

function slideChange(index) {
    const carousels = document.querySelectorAll('.flickity-enabled.product-card-images-slider');
    if (carousels.length === 0) return false;

    carousels.forEach(carousel => {
        const flkty = getFlickityInstance(carousel);
        if (flkty) {
            flkty.select(index, true, true);
        }
    });

    return true;
}

function initSlideChange(index) {
    if (modelViewCarouselTimer) clearInterval(modelViewCarouselTimer);
    if (slideChange(index)) return;

    modelViewCarouselTimer = setInterval(() => {
        if (slideChange(index)) {
            clearInterval(modelViewCarouselTimer);
            modelViewCarouselTimer = null;
        }
    }, 500);
}

function applyModelViewState(checkbox, waitForCarousels = false) {
    const container = checkbox.closest(MODEL_VIEW_CONTAINER_SELECTOR);
    if (!container) return;

    container.classList.toggle(MODEL_VIEW_ACTIVE_CLASS, checkbox.checked);

    if (checkbox.checked) {
        localStorage.setItem('showItemsWithoutModel', 'true');
    } else {
        localStorage.removeItem('showItemsWithoutModel');
    }

    const slideIndex = checkbox.checked ? 1 : 0;
    if (waitForCarousels) {
        initSlideChange(slideIndex);
    } else {
        slideChange(slideIndex);
    }
}

function initCheckboxState(checkbox) {
    if (initializedModelViewCheckboxes.has(checkbox)) return;
    initializedModelViewCheckboxes.add(checkbox);

    checkbox.checked = localStorage.getItem('showItemsWithoutModel') === 'true';
    applyModelViewState(checkbox, true);
}

function initCheckboxes(root) {
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;

    if (root.matches?.(MODEL_VIEW_CHECKBOX_SELECTOR)) initCheckboxState(root);
    root.querySelectorAll?.(MODEL_VIEW_CHECKBOX_SELECTOR).forEach(initCheckboxState);
}

class ShowItemsWithoutModelSwitch extends HTMLElement {
    constructor() {
        super();
        const checkbox = this.querySelector(MODEL_VIEW_CHECKBOX_SELECTOR);
        if (checkbox) initCheckboxState(checkbox);
    }
}

if (!customElements.get('show-items-without-model')) {
    customElements.define('show-items-without-model', ShowItemsWithoutModelSwitch);
}

document.addEventListener('change', event => {
    if (!event.target.matches?.(MODEL_VIEW_CHECKBOX_SELECTOR)) return;
    applyModelViewState(event.target);
});

// Capture on window so Nosto cannot swallow the key event from an ancestor
// before it reaches the rendered checkbox. Space and Enter both follow the
// same click path as pointer activation.
window.addEventListener('keydown', event => {
    if (event.repeat || !event.target.matches?.(MODEL_VIEW_CHECKBOX_SELECTOR)) return;
    if (event.key !== 'Enter' && event.code !== 'Enter' && event.key !== ' ' && event.code !== 'Space') return;

    event.preventDefault();
    event.target.click();
}, true);

initCheckboxes(document);

new MutationObserver(mutations => {
    mutations.forEach(mutation => {
        mutation.addedNodes.forEach(initCheckboxes);
    });
}).observe(document.documentElement, { childList: true, subtree: true });
